import React, { useEffect, useRef } from "react";
import { Modal } from "bootstrap";
import Button from "../Button/Button";

interface CustomModalProps {
  show: boolean;
  handleClose?: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  footerContent?: React.ReactNode;
  className?: string;
  isHeaderVisible?: boolean;
  isFooterVisible?: boolean;
  isCrossBtn?: boolean;
  backdrop?: boolean | "static";
  keyboard?: boolean;
}

const CustomModal = ({
  show,
  handleClose,
  title,
  children,
  footerContent,
  className = "",
  isHeaderVisible = true,
  isFooterVisible = true,
  isCrossBtn = true,
  backdrop = "static",
  keyboard = false,
}: CustomModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalInstance = useRef<Modal | null>(null);
  const wasShownRef = useRef(false);

  const forceCleanup = () => {
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
    document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
  };

  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    modalInstance.current = new Modal(el, { backdrop, keyboard });
    return () => {
      wasShownRef.current = false;
      // Remove fade so Bootstrap's transition callbacks fire synchronously,
      // before dispose() nulls this._element and causes _resetAdjustments crash.
      el.classList.remove("show", "fade");
      el.style.display = "none";
      modalInstance.current?.dispose();
      modalInstance.current = null;
      forceCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Guard against StrictMode's double-invocation: if the element has been
    // detached from the DOM during cleanup, skip the show/hide call.
    if (!modalInstance.current || !modalRef.current?.isConnected) return;
    if (show) {
      wasShownRef.current = true;
      modalInstance.current.show();
    } else if (wasShownRef.current) {
      // Only call hide() if the modal was actually shown — calling hide() on a
      // never-shown modal still triggers Bootstrap's backdrop transition which
      // can fire _resetAdjustments after dispose() nulls this._element.
      wasShownRef.current = false;
      modalInstance.current.hide();
    }
  }, [show]);

  useEffect(() => {
    const modalEl = modalRef.current;
    if (!modalEl) return;

    const handleHidden = () => {
      handleClose?.();
    };

    modalEl.addEventListener("hidden.bs.modal", handleHidden);
    return () => {
      modalEl.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [handleClose]);

  return (
    <div className={`modal fade ${className}`} ref={modalRef} tabIndex={-1}>
      <div className="modal-dialog">
        <div className="modal-content">
          {isHeaderVisible && (
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              {isCrossBtn && (
                <Button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                />
              )}
            </div>
          )}
          <div className="modal-body">{children}</div>
          {isFooterVisible && (
            <div className="modal-footer">{footerContent}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomModal;
